"use client";

import { useState, useCallback, useRef, useEffect } from "react";
import {
  generateVideo,
  getVideoStatus,
  VideoStatusResponse,
} from "@/lib/api";

const POLL_INTERVAL = 5000; // 5 seconds

interface UseVideoGenerationOptions {
  onComplete?: (video: VideoStatusResponse) => void;
  onError?: (error: string) => void;
}

export function useVideoGeneration(options?: UseVideoGenerationOptions) {
  const [isGenerating, setIsGenerating] = useState(false);
  const [currentVideoId, setCurrentVideoId] = useState<string | null>(null);
  const [videoStatus, setVideoStatus] = useState<VideoStatusResponse | null>(null);
  const [error, setError] = useState<string | null>(null);

  const pollIntervalRef = useRef<NodeJS.Timeout | null>(null);
  const isMountedRef = useRef(true);

  // Cleanup on unmount
  useEffect(() => {
    isMountedRef.current = true;
    return () => {
      isMountedRef.current = false;
      if (pollIntervalRef.current) {
        clearInterval(pollIntervalRef.current);
      }
    };
  }, []);

  const stopPolling = useCallback(() => {
    if (pollIntervalRef.current) {
      clearInterval(pollIntervalRef.current);
      pollIntervalRef.current = null;
    }
  }, []);

  const pollStatus = useCallback(
    async (videoId: string) => {
      if (!isMountedRef.current) return;

      try {
        const status = await getVideoStatus(videoId);

        if (!isMountedRef.current) return;

        setVideoStatus(status);

        if (status.status === "completed") {
          stopPolling();
          setIsGenerating(false);
          options?.onComplete?.(status);
        } else if (status.status === "failed") {
          stopPolling();
          setIsGenerating(false);
          setError(status.error_message || "Video generation failed");
          options?.onError?.(status.error_message || "Video generation failed");
        }
        // Keep polling for pending/processing status
      } catch (err) {
        if (!isMountedRef.current) return;
        console.error("Error polling video status:", err);
        // Continue polling even on error
      }
    },
    [stopPolling, options]
  );

  const startPolling = useCallback(
    (videoId: string) => {
      stopPolling();

      // Initial poll
      pollStatus(videoId);

      // Set up interval
      pollIntervalRef.current = setInterval(() => {
        pollStatus(videoId);
      }, POLL_INTERVAL);
    },
    [pollStatus, stopPolling]
  );

  const generate = useCallback(
    async (imageId: string, prompt?: string) => {
      setIsGenerating(true);
      setError(null);
      setVideoStatus(null);

      try {
        const response = await generateVideo({
          image_id: imageId,
          prompt,
        });

        setCurrentVideoId(response.video_id);
        setVideoStatus({
          video_id: response.video_id,
          status: response.status as "pending" | "processing" | "completed" | "failed",
        });

        // Start polling for status updates
        startPolling(response.video_id);

        return response.video_id;
      } catch (err) {
        const errorMessage = err instanceof Error ? err.message : "Failed to generate video";
        setError(errorMessage);
        setIsGenerating(false);
        options?.onError?.(errorMessage);
        throw err;
      }
    },
    [startPolling, options]
  );

  const reset = useCallback(() => {
    stopPolling();
    setIsGenerating(false);
    setCurrentVideoId(null);
    setVideoStatus(null);
    setError(null);
  }, [stopPolling]);

  return {
    isGenerating,
    currentVideoId,
    videoStatus,
    error,
    generate,
    reset,
    stopPolling,
  };
}
